/**
 * Portfolio Store
 *
 * State management for portfolios.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { portfolioApi } from '../api/portfolio.api'
import type { Portfolio } from '../types/portfolio.types'

interface PortfolioState {
  portfolios: Portfolio[]
  selectedPortfolio: Portfolio | null
  isLoading: boolean
  error: string | null

  fetchPortfolios: (userId: string) => Promise<void>
  fetchPortfolioById: (id: string) => Promise<void>
  createPortfolio: (data: Partial<Portfolio> & { userId: string }) => Promise<Portfolio | null>
  updatePortfolio: (id: string, data: Partial<Portfolio>) => Promise<boolean>
  deletePortfolio: (id: string) => Promise<boolean>
}

export const usePortfolioStore = createWithEqualityFn<PortfolioState>((set) => ({
  portfolios: [],
  selectedPortfolio: null,
  isLoading: false,
  error: null,

  fetchPortfolios: async (userId: string) => {
    set({ isLoading: true, error: null })
    const res = await portfolioApi.getPortfolios(userId)
    if (res.success) {
      set({ portfolios: res.data, isLoading: false })
    } else {
      set({ error: res.error ?? 'Failed to fetch portfolios', isLoading: false })
    }
  },

  fetchPortfolioById: async (id: string) => {
    set({ isLoading: true, error: null })
    const res = await portfolioApi.getPortfolioById(id)
    if (res.success) {
      set({ selectedPortfolio: res.data, isLoading: false })
    } else {
      set({ error: res.error ?? 'Portfolio not found', isLoading: false })
    }
  },

  createPortfolio: async (data) => {
    const res = await portfolioApi.createPortfolio(data)
    if (res.success) {
      set(state => ({ portfolios: [...state.portfolios, res.data] }))
      return res.data
    }
    return null
  },

  updatePortfolio: async (id, data) => {
    const res = await portfolioApi.updatePortfolio(id, data)
    if (res.success) {
      set(state => ({
        portfolios: state.portfolios.map(p => p.id === id ? res.data : p),
        selectedPortfolio: state.selectedPortfolio?.id === id ? res.data : state.selectedPortfolio,
      }))
      return true
    }
    return false
  },

  deletePortfolio: async (id) => {
    const res = await portfolioApi.deletePortfolio(id)
    if (res.success) {
      set(state => ({
        portfolios: state.portfolios.filter(p => p.id !== id),
        selectedPortfolio: state.selectedPortfolio?.id === id ? null : state.selectedPortfolio,
      }))
      return true
    }
    return false
  },
}))

export const selectPortfolios = (s: PortfolioState) => s.portfolios
export const selectSelectedPortfolio = (s: PortfolioState) => s.selectedPortfolio
